export function parseDeviceCode(
  status: number,
  body: unknown
): {
  deviceCode: string;
  userCode: string;
  url: string;
  intervalSec: number;
  expiresInSec: number;
} {
  if (status === 429) {
    throw new Error('Too many login attempts; wait a few minutes and retry');
  }
  if (status === 404 || status === 401 || status === 503) {
    throw new Error(
      `Browser sign-in is not available on the server yet (status ${status}); use \`spacecode login --api-key\` instead`
    );
  }
  if (status !== 200) {
    throw new Error(`Could not start login: status ${status}`);
  }

  if (typeof body !== 'object' || body === null) {
    throw new Error(`Could not start login: status ${status}`);
  }

  const obj = body as Record<string, unknown>;

  const requiredStringFields = [
    'device_code',
    'user_code',
    'verification_uri',
  ] as const;

  for (const field of requiredStringFields) {
    const value = obj[field];
    if (typeof value !== 'string') {
      throw new Error(`Could not start login: status ${status}`);
    }
  }

  const interval = obj['interval'];
  if (typeof interval !== 'number' || interval <= 0) {
    throw new Error(`Could not start login: status ${status}`);
  }

  const expiresIn = obj['expires_in'];
  if (typeof expiresIn !== 'number' || expiresIn <= 0) {
    throw new Error(`Could not start login: status ${status}`);
  }

  const verificationUriComplete =
    typeof obj['verification_uri_complete'] === 'string'
      ? (obj['verification_uri_complete'] as string).trim()
      : '';
  const url =
    verificationUriComplete !== ''
      ? verificationUriComplete
      : (obj['verification_uri'] as string);

  return {
    deviceCode: obj['device_code'] as string,
    userCode: obj['user_code'] as string,
    url,
    intervalSec: interval,
    expiresInSec: expiresIn,
  };
}

export function classifyTokenResponse(
  status: number,
  body: unknown,
  intervalSec: number
):
  | { kind: 'done'; key: string; keyPrefix?: string }
  | { kind: 'pending'; intervalSec: number } {
  if (status === 200) {
    if (typeof body === 'object' && body !== null) {
      const b = body as Record<string, unknown>;
      if (typeof b.key === 'string' && b.key.startsWith('sk_')) {
        const result: {
          kind: 'done';
          key: string;
          keyPrefix?: string;
        } = {
          kind: 'done',
          key: b.key as string,
        };
        if (typeof b.keyPrefix === 'string') {
          result.keyPrefix = b.keyPrefix as string;
        }
        return result;
      }
    }
    throw new Error('Login returned an invalid key');
  }

  const isObjectBody = typeof body === 'object' && body !== null;
  const error = isObjectBody ? (body as Record<string, unknown>).error : undefined;

  if (error === 'authorization_pending') {
    return { kind: 'pending', intervalSec };
  }

  if (error === 'slow_down') {
    return { kind: 'pending', intervalSec: intervalSec + 5 };
  }

  if (error === 'expired_token') {
    throw new Error('Login code expired');
  }

  if (error === 'access_denied') {
    throw new Error('Login was denied');
  }

  if (error === 'invalid_grant') {
    throw new Error('Login code is no longer valid; run `spacecode login` again');
  }
  if (error === 'max_keys_reached') {
    throw new Error('Your organization has reached its API key limit; revoke an old key in the dashboard and retry');
  }
  const errMsg = typeof error === 'string' ? `: ${error}` : '';
  throw new Error(`Login failed (${status})${errMsg}`);
}
